/**
 * SitePowerOutageBanner — banner vermelho no topo do Dashboard listando
 * sites em queda geral de energia (#162 `site_power_outage`).
 *
 * O texto não diz "Camada 2" nem "cross-device". São nomes do nosso desenho
 * interno — para quem lê, o que importa é o que aconteceu (várias tomadas do
 * mesmo site calaram juntas) e por que o resto ficou quieto. Cliente que
 * precisa aprender o vocabulário do fornecedor para entender o próprio
 * alarme está lendo a documentação errada.
 *
 * Diferente de alertas normais (por rule/equipment), esses são cross-device
 * agregados por site — ficam em tabela separada. Sem esse widget, cliente
 * só descobre pela mensagem WhatsApp (ou se olhar direto no banco).
 *
 * Regra de renderização:
 *   - Se nenhum site em firing → não renderiza nada (retorna null)
 *   - Se 1+ sites em firing → banner vermelho com contagem + lista
 */

import { AlertOctagon, Zap } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useActiveSiteAlerts } from "@/hooks/queries";
import { timeAgo } from "@/lib/format";

export function SitePowerOutageBanner() {
  const auth = useAuth();
  // super_admin sem tenant → vê queda em todos os tenants
  const q = useActiveSiteAlerts(undefined);

  const outages = q.data ?? [];
  if (outages.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-red-300 bg-red-50 p-4 shadow-sm
                 dark:border-red-900/60 dark:bg-red-950/30"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertOctagon className="h-6 w-6 flex-none text-red-600 dark:text-red-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-red-800 dark:text-red-200">
            Queda geral de energia — {outages.length} site
            {outages.length > 1 ? "s" : ""} afetado{outages.length > 1 ? "s" : ""}
          </div>
          <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-300/80">
            Várias tomadas do mesmo site pararam de reportar ao mesmo tempo.
            Enquanto isso, os avisos de cada ativo ficam reunidos aqui, para
            não repetir o mesmo problema várias vezes.
          </p>

          <ul className="mt-3 space-y-1.5">
            {outages.map((o) => (
              <li
                key={o.site_id}
                className="flex flex-wrap items-center gap-2 text-sm
                           text-red-900 dark:text-red-100"
              >
                <Zap className="h-3.5 w-3.5" />
                <span className="font-medium">{o.site_name}</span>
                {auth.isSuperAdmin && (
                  <span
                    className="rounded bg-red-200/60 px-1.5 py-0.5 text-[10px]
                               font-semibold uppercase text-red-800
                               dark:bg-red-500/20 dark:text-red-200"
                  >
                    {o.tenant_slug}
                  </span>
                )}
                {typeof o.ac_offline_count === "number" &&
                  typeof o.ac_total === "number" && (
                    <span className="text-xs text-red-700/80 dark:text-red-300/80">
                      · {o.ac_offline_count}/{o.ac_total} tomadas offline
                    </span>
                  )}
                <span className="text-xs text-red-700/70 dark:text-red-300/70">
                  · há {timeAgo(o.since)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
